import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

interface Bike {
  id: string;
  site_id?: string;
  user_location_id?: string;
  assignedLocation?: string;
  site?: {
    name: string;
  };
  users?: {
    username: string;
  };
}

interface BikeLocationChartProps {
  isLoading: boolean;
  bikes: any[];
}

const BikeLocationChart: React.FC<BikeLocationChartProps> = ({ isLoading, bikes }) => {
  const locationData = React.useMemo(() => {
    if (!bikes || bikes.length === 0) return [];
    
    const locationCounts: Record<string, {
      bikes: number;
      rides: number;
    }> = {};

    bikes.forEach((bike) => {
      // Determine location based on site_id and user_location_id
      let locationKey = 'Unassigned';
      
      // If bike has site_id, it's at a site location
      if (bike.site_id) {
        // Use site name if available from joined data, otherwise use "Site"
        locationKey = bike.site?.name || 'Site';
      } 
      // If bike has user_location_id, it's at a user's home
      else if (bike.user_location_id) {
        // Use the username + "'s Home" if available, otherwise use "User Home"
        const username = bike.users?.username;
        locationKey = username ? `${username}'s Home` : 'User Home';
      }
      
      // For frontend-mapped bikes that don't have site_id/user_location_id fields
      // but do have assignedLocation from the mapping
      if (!bike.site_id && !bike.user_location_id && bike.assignedLocation) {
        locationKey = bike.assignedLocation;
      }
      
      if (!locationCounts[locationKey]) {
        locationCounts[locationKey] = {
          bikes: 0,
          rides: 0
        };
      }
      
      locationCounts[locationKey].bikes += 1;
      // Calculate approximate rides based on location (for demonstration)
      locationCounts[locationKey].rides = Math.floor(locationCounts[locationKey].bikes * (Math.random() * 10 + 5));
    });

    return Object.entries(locationCounts)
      .filter(([name]) => name !== 'Unassigned')
      .map(([name, counts]) => ({
        name,
        bikes: counts.bikes,
        rides: counts.rides
      }))
      .slice(0, 6); // Limit to top 6 locations
  }, [bikes]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>eBikes per Location</CardTitle>
        <CardDescription>
          Distribution of eBikes across different locations
        </CardDescription>
      </CardHeader>
      <CardContent className="h-[300px]">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : locationData.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-muted-foreground">No location data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={locationData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip />
              <Legend />
              <Bar dataKey="bikes" name="Number of eBikes" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default BikeLocationChart;
